import React from 'react';
import DefaultLayout from '../../../layouts/DefaultLayout';
import TemplateColumn, { TemplateColumnWithSidebarContainer } from '../../../layouts/TemplateColumn';
import { PageHeader, PageSection, RelatedContent, CrossLink, IndicationPageIntro, AnchorLinks, TextBlock, DataTable, PageSubSection } from '../../../components';
import GlobalSafetyData, { globalSafetyBlock } from './_global_safety_data'
import { jobCodes } from '../../../util/data/jobCodes';
import { StaticImage } from 'gatsby-plugin-image';
import KNSafetyMob from "../../../assets/KN-689-safety-small-v-2.svg"


// SEO INFORMATION 
const pageMeta = {
    title: `KEYNOTE-689 - Adverse Reactions & Safety Data for This Neoadjuvant Followed by Adjuvant Treatment Regimen for Resectable Locally Advanced HNSCC`,
    keywords: `keynote 689, adverse reactions`,
    description: `Learn about immune-mediated adverse reactions, their management, and safety data from the KEYNOTE-689 clinical trial for this neoadjuvant followed by adjuvant treatment regimen.`,
    schemaJsonLD: [
    `{"@context":"https://schema.org/","url":"https://www.keytrudahcp.com/safety/adverse-reactions/hnscc-squamous-cell-carcinoma/","@type":"MedicalAudience","audienceType":"US Health Care Professionals","geographicArea":"This site is intended for health care professionals of the United States, its territories, and Puerto Rico."}`,
    `{"@context":"https://schema.org/","url":"https://www.keytrudahcp.com/safety/adverse-reactions/hnscc-squamous-cell-carcinoma/","@type":"MedicalWebPage","name":" Most Common Adverse Reactions in KEYNOTE⁠-⁠689 for KEYTRUDA","description":"The safety of KEYTRUDA as neoadjuvant and adjuvant treatment added to SOC was investigated in patients with resectable locally advanced (stage III–IVA) head and neck squamous cell carcinoma. A total of 361 patients received treatment on the KEYTRUDA arm and 315 patients received treatment on the SOC arm."}`,
    ]
}

//from /src/util/data/indications.js
// UPDATE THIS TO THE CORRECT INDICATION #
const indicationId = 38;

const jobCode = jobCodes[37].jobCode;

const anchorLinkModelData = [
    {label: 'Incidence of Selected Immune-Mediated Adverse Reactions'},
    {label: 'Management of Selected Immune-Mediated Adverse Reactions'},
    {label: 'Most Common Adverse Reactions in KEYNOTE&#8288;-&#8288;689 for KEYTRUDA'}
]


const relatedContent = [
    {
        section: 'Resources',
        link: {
            url: '/resources/mechanism-of-action/',
            label: 'Mechanism of Action'
        }
    },
    {
        section: 'Resources',
        link: {
            url: 'https://www.merckaccessprogram-keytruda.com/',
            label: 'Access & Reimbursement Support'
        }
    }
];

const crossLinkContent = {
    sections : [
        {
            heading: "Additional Safety Data",
            items: [
                { text: 'HNSCC 1L Combo Therapy', url: '/safety/adverse-reactions/hnscc-first-line-combination-therapy/' },
                { text: 'HNSCC 1L Mono Therapy', url: '/safety/adverse-reactions/hnscc-first-line-monotherapy/' },
                ]
        },
        {
            heading: "Related Information",
            items: [
                { text: 'Treatment Monitoring & Management', url: '/safety/monitoring-managing-adverse-reactions/' },
                { text: 'Dose Modifications', url: '/dosing/dose-modifications/' },
                ]
        },
    ],
    interestLinks : {
        heading: "You Might Be Interested In",
        items: [
            { eyebrow: 'Resources', text: 'Mechanism of Action', url: '/resources/mechanism-of-action/' },
        ],
    }
};

const commonTableAllGradesFootnotes = [{
    label: 'a.',
    text: 'Graded per NCI CTCAE v4.03.'
},
{
    label: 'b.',
    text: 'Includes pharyngeal inflammation, cheilitis, oral mucosal erythema, mucosal inflammation, glossitis, mouth ulceration, tongue ulceration.',
},
{
    label: 'c.',
    text: 'Includes colitis, enteritis, hemorrhagic diarrhea.',
},
{
    label: 'd.',
    text: 'Includes blood thyroid stimulating hormone increased.',
},
{
    label: 'e.',
    text: 'Includes asthenia.',
},
{
    label: 'f.',
    text: 'Includes dermatitis, skin exfoliation, dermatitis acneiform, eczema, rash macular, rash maculo-papular, erythrema multiforme, dermatitis exfoliative, urticarial dermatitis, eczema asteatotic, exfoliative rash, rash pruritic, rash pustular.',
},
{
    label: 'g.',
    text: 'Includes neck pain, arthralgia, pain in extremity, back pain, myalgia, bone pain, arthritis, non-cardiac chest pain, musculoskeletal chest pain, musculoskeletal stiffness, spinal pain.',
},
]


const Page = ({ location }) => {

    return (
        <DefaultLayout indicationId={indicationId} pageMeta={pageMeta} location={location} jobCode={jobCode}>
            <div data-template-name="template-e">
                {/* Begin Intro Column */}
                <TemplateColumn id="template-e-intro" maxWidth={824}>
                    <PageHeader title="Selected Adverse Reactions" isBranding />
                    <PageSection bgColor="white">
                        <IndicationPageIntro indicationId={indicationId} />
                    </PageSection>
                </TemplateColumn>
                {/* End Intro Column */}

                <TemplateColumnWithSidebarContainer>
                    {/* Begin Main Column */}
                    <TemplateColumn id="template-e-main">
                        <PageSection bgColor="cloud" title="Severe and Fatal Immune-Mediated Adverse Reactions">
                            <TextBlock>{globalSafetyBlock}</TextBlock>
                            <AnchorLinks items={anchorLinkModelData} />
                        </PageSection>

                        <GlobalSafetyData />

                        <PageSection itle={anchorLinkModelData[2].label}>
                            <PageSubSection title="Most Common Adverse Reactions in KEYNOTE-689 for KEYTRUDA">
                                <TextBlock>
                                    <p>The safety of KEYTRUDA as neoadjuvant and adjuvant treatment added to SOC was investigated in patients with resectable locally advanced (stage III–IVA) head and neck squamous cell carcinoma.</p>
                                    <ul>
                                        <li>A total of 361 patients received treatment on the KEYTRUDA arm and 315 patients received treatment on the SOC arm.</li>
                                    </ul>
                                    <strong>Neoadjuvant Phase:</strong>
                                    <ul>
                                        <li>The median duration of exposure to KEYTRUDA in the neoadjuvant phase was 3.1 weeks (range: 1 day to 4.9 weeks).</li>
                                        <li>Of the 361 patients who received at least one dose of single agent KEYTRUDA as neoadjuvant treatment, 11% of patients experienced serious adverse reactions. Serious adverse reactions that occurred in more than one patient were pneumonia (1.4%), tumor hemorrhage (0.8%), dysphagia (0.6%), immune-mediated hepatitis (0.6%), cellulitis (0.6%), and dyspnea (0.6%). Fatal adverse reactions occurred in 1.1% of patients who received neoadjuvant KEYTRUDA including respiratory failure, clostridium infection, septic shock, and myocardial infarction (one patient each).</li>
                                        <li>Permanent discontinuation of KEYTRUDA due to an adverse reaction occurred in 2.8% of patients who received KEYTRUDA as neoadjuvant treatment. The most frequent adverse reaction which resulted in permanent discontinuation of neoadjuvant KEYTRUDA in more than one patient was arthralgia (0.6%).</li>
                                        <li>Of the 361 patients who received KEYTRUDA as neoadjuvant treatment, 11% (n=38) did not receive surgery. Surgical cancellation on the KEYTRUDA arm was due to disease progression in 4% (n=15), patient decision in 3% (n=10), adverse reactions in 1.4% (n=5), physician’s decision in 1.1% (n=4), unresectable tumor in 0.6% (n=2), loss of follow-up in 0.3% (n=1), and use of non-study anti-cancer therapy in 0.3% (n=1).</li>
                                        <li>Of the 351 patients randomized to SOC, 12% (n=43) did not receive surgery. Surgical cancellation on the SOC arm was due to patient decision in 7% (n=24), physician’s decision in 2.3% (n=8), disease progression in 1.7% (n=6), and adverse reactions in 1.4% (n=5).</li>
                                        <li>Of the 323 KEYTRUDA-treated patients who received surgery, 1.2% (n=4) experienced delay of surgery (defined as on-study surgery occurring ≥ 9 weeks after initiation of neoadjuvant KEYTRUDA) due to adverse reactions. Of the 307 patients randomized to SOC who received surgery on study, 0.3% (n=1) experienced delay of surgery (defined as surgery occurring ≥ 6 weeks after randomization) due to adverse reactions.</li>
                                        <li>Among the KEYTRUDA-treated patients who received surgery, 2.8% (n=9) did not receive adjuvant treatment due to adverse reactions. Among the SOC-treated patients who received surgery, 3.6% (n=11) did not receive adjuvant RT or chemoradiation due to adverse reactions.</li>
                                    </ul>
                                    <strong>Adjuvant Phase:</strong>
                                    <ul>
                                        <li>The median duration of exposure to KEYTRUDA in the adjuvant phase was 42 weeks (range: 1 day to 82 weeks).</li>
                                        <li>A total of 275 patients in the KEYTRUDA arm and 275 patients in the SOC arm started the adjuvant phase of treatment. On the KEYTRUDA arm, 100 patients received KEYTRUDA and cisplatin with concurrent RT, 154 patients received KEYTRUDA alone with concurrent RT, 7 patients received cisplatin alone with concurrent RT, and 13 patients received RT alone. One patient received KEYTRUDA alone. On the SOC arm, 139 patients received cisplatin with concurrent RT while 136 patients received RT alone. For the KEYTRUDA arm, a total of 222 patients received single-agent KEYTRUDA following RT.</li>
                                        <li>Of the 255 patients who received at least one dose of KEYTRUDA in the adjuvant phase, 38% experienced serious adverse reactions. The most frequent serious adverse reactions reported in ≥1% of KEYTRUDA-treated patients were pneumonia (2.7%), pyrexia (2.4%), stomatitis (2.4%), acute kidney injury (2.0%), pneumonitis (1.6%), COVID-19 (1.2%), death not otherwise specified (1.2%), diarrhea (1.2%), dysphagia (1.2%), gastrostomy tube site complication (1.2%), and immune-mediated hepatitis (1.2%).</li>
                                        <li>Fatal adverse reactions occurred in 5% including death not otherwise specified (1.2%), acute renal failure (0.4%), hypercalcemia (0.4%), pulmonary hemorrhage (0.4%), dysphagia/malnutrition (0.4%), mesenteric thrombosis (0.4%), sepsis (0.4%), pneumonia (0.4%), COVID-19 (0.4%), respiratory failure (0.4%), cardiovascular disorder (0.4%), and gastrointestinal hemorrhage (0.4%).</li>
                                        <li>Permanent discontinuation of adjuvant KEYTRUDA due to an adverse reaction occurred in 17% of patients. The most frequent (≥1%) adverse reactions that led to permanent discontinuation of adjuvant KEYTRUDA were pneumonitis, colitis, immune-mediated hepatitis, and death not otherwise specified. Of the 275 patients who received SOC in the adjuvant phase, 23% experienced serious adverse reactions.</li>
                                        <li>The most frequent serious adverse reactions reported in &gt;1% of SOC-treated patients were pneumonia (3.6%) and acute kidney injury (3.3%). Fatal adverse reactions occurred in 4.7% including pneumonia (0.7%), septic shock (0.4%), death not otherwise specified (0.4%), sudden death (0.4%), myocardial infarction (0.4%), pancreatic neoplasm (0.4%), and other infections (2.9%).</li>
                                    </ul>
                                </TextBlock>
                                <DataTable
                                    title="Adverse Reactions Occurring in ≥20% of Patients Receiving KEYTRUDA in KEYNOTE-689"
                                    mobileImage={() => <img src={KNSafetyMob} style={{ width: '100%', maxWidth: '500px', margin: '0 auto' }} alt='' />}
                                    desktopImage={() => <StaticImage quality={90} placeholder="DOMINANT_COLOR" src="../../../assets/KN-689-safety-x-large-v-2.png" alt='Common Adverse Reactions Occurring in KEYNOTE-689' />}
                                    footnotes={commonTableAllGradesFootnotes}
                                    definitions="NCI CTCAE = National Cancer Institute Common Terminology Criteria for Adverse Events; SOC = standard of care."
                                    limitation=""
                                />
                            </PageSubSection>

                            {/* <TextBlock>
                            The most common adverse reactions for KEYTRUDA (reported in ≥20% of patients) were fatigue, musculoskeletal pain, decreased appetite, pruritus, diarrhea, nausea, rash, pyrexia, cough, dyspnea, constipation, pain, and abdominal pain.
                            </TextBlock> */}
                        </PageSection>

                        {/* <PageSection>
                            <PageSubSection title="Selected Laboratory Abnormalities Worsened From Baseline Occurring in ≥15% of cHL Patients Receiving KEYTRUDA">
                                <DataTable
                                    title=""
                                    mobileImage={() => <img src='../../../assets/KN-689-laboratory-abnormalities-small-v-1.svg' style={{ width: '100%', maxWidth: '500px', margin: '0 auto' }} alt='' />}
                                    desktopImage={() => <StaticImage quality={90} placeholder="DOMINANT_COLOR" src="../../../assets/KN-689-laboratory-abnormalities-x-large.png" alt='' />}
                                    footnotes=""
                                    definitions=""
                                />
                            </PageSubSection>
                        </PageSection> */}
                    </TemplateColumn>
                    {/* End Main Column */}

                    <TemplateColumn id="template-e-sidebar">
                        <PageSection bgColor="mist">
                            <CrossLink {...crossLinkContent} />
                        </PageSection>
                    </TemplateColumn>

                </TemplateColumnWithSidebarContainer>

                <TemplateColumn id="template-e-secondary">
                    <PageSection bgColor="keytrudaGreen">
                        <RelatedContent items={relatedContent} />
                    </PageSection>
                </TemplateColumn>
            </div>
        </DefaultLayout>
    );
};

export default Page;